/**
 * BioburdenVisitorsPanel — admin listing of every visitor who unlocked the
 * /services/bioburden landing page (via the business-email gate).
 *
 * Backed by GET /api/bioburden/visitors (super_admin / hub_admin).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Users,
  Mail,
  Phone,
  Building2,
  Briefcase,
  RefreshCw,
  Search,
  Download,
  Clock,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return dt; }
}

export default function BioburdenVisitorsPanel() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/bioburden/visitors?limit=1000`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setRows(json.visitors || []);
    } catch (e) {
      toast.error("Could not load visitors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.full_name, r.email, r.phone, r.company, r.role_title, r.email_domain]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [rows, query]);

  const exportCsv = () => {
    const header = ["Captured", "Full name", "Email", "Domain", "Phone", "Facility", "Role", "Hits", "Last seen", "Referer"];
    const lines = [header.join(",")];
    for (const r of filtered) {
      const cells = [
        fmt(r.created_at),
        r.full_name || "",
        r.email || "",
        r.email_domain || "",
        r.phone || "",
        r.company || "",
        r.role_title || "",
        String(r.hits ?? 1),
        fmt(r.last_seen_at),
        r.referer || "",
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`);
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bioburden-visitors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="bioburden-visitors-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#b91c1c]" />
            Bioburden Landing Visitors
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Every hospital / clinic contact who unlocked <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">/services/bioburden</code>. Personal emails are blocked at capture.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search name, email, facility…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 w-72"
              data-testid="bioburden-visitors-search"
            />
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="bioburden-visitors-export">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" onClick={load} data-testid="bioburden-visitors-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total captured" value={rows.length} />
        <StatCard label="Unique domains" value={new Set(rows.map((r) => r.email_domain).filter(Boolean)).size} />
        <StatCard label="With facility" value={rows.filter((r) => r.company).length} />
        <StatCard label="Repeat visitors" value={rows.filter((r) => (r.hits || 1) > 1).length} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Captured</Th>
                <Th>Name</Th>
                <Th>Work email</Th>
                <Th>Phone</Th>
                <Th>Facility</Th>
                <Th>Role</Th>
                <Th className="text-right">Hits</Th>
                <Th>Auto-reply</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">No visitors yet.</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`visitor-row-${r.visitor_id}`}>
                  <Td>
                    <div className="flex items-center gap-1 text-slate-500 text-xs">
                      <Clock className="w-3 h-3" /> {fmt(r.created_at)}
                    </div>
                  </Td>
                  <Td className="font-semibold text-slate-900">{r.full_name || "—"}</Td>
                  <Td>
                    <a href={`mailto:${r.email}`} className="text-slate-700 hover:text-[#b91c1c] flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5 text-slate-400" /> {r.email}
                    </a>
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {r.email_domain || "—"}
                    </div>
                  </Td>
                  <Td>
                    <a href={`tel:${(r.phone || "").replace(/\s+/g, "")}`} className="text-slate-700 hover:text-[#b91c1c] flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-slate-400" /> {r.phone || "—"}
                    </a>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span>{r.company || "—"}</span>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1 text-slate-700">
                      <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                      <span>{r.role_title || "—"}</span>
                    </div>
                  </Td>
                  <Td className="text-right font-semibold">{r.hits ?? 1}</Td>
                  <Td><ConfirmationPill status={r.confirmation_status} sentAt={r.confirmation_sent_at} err={r.confirmation_error} /></Td>
                  <Td className="text-slate-500 text-xs">{fmt(r.last_seen_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

function ConfirmationPill({ status, sentAt, err }) {
  const s = (status || "").toLowerCase();
  if (!s || s === "queued") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200" title="Auto-reply queued">
        Queued
      </span>
    );
  }
  if (s === "success" || s === "sent") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200" title={sentAt ? `Sent ${fmt(sentAt)}` : "Sent"}>
        Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200" title={err || "Send failed"}>
      Failed
    </span>
  );
}
